// ============= Imports =============
import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  useRef,
} from 'react';
import { useMsal } from '@azure/msal-react';
import {
  AccountInfo,
  InteractionRequiredAuthError,
  IPublicClientApplication,
} from '@azure/msal-browser';
import { isAuthBypassed } from '../authConfig';
import { tokenManager, createLogger } from '@tw/tw-component-library';

// ============= Types =============
export interface CurrentUser {
  userId: string;
  userDisplayName: string;
  userEmail: string;
  // JWT token claims
  aud?: string;
  iss?: string;
  iat?: number;
  nbf?: number;
  exp?: number;
  name?: string;
  nonce?: string;
  oid?: string;
  preferred_username?: string;
  rh?: string;
  sid?: string;
  sub?: string;
  tid?: string;
  uti?: string;
  ver?: string;
  // Additional claims that might be present
  [key: string]: unknown;
}

export interface AuthContextProps {
  isAuthenticated: boolean;
  isInitializing: boolean;
  activeAccount: AccountInfo | null;
  accessToken: string | null;
  currentUser: CurrentUser | null;
  login: () => void;
  logout: () => void;
  getAccessToken: (
    scopes?: string[],
    options?: { skipStorageToken?: boolean }
  ) => Promise<string | null>;
}

interface AuthState {
  isAuthenticated: boolean;
  activeAccount: AccountInfo | null;
  accessToken: string | null;
  currentUser: CurrentUser | null;
}

interface TokenResponse {
  token: string;
  expiresOn: Date;
}

// ============= Constants =============
const DEFAULT_SCOPES = ['openid', 'profile'];
const TOKEN_CACHE_KEY = 'tokenCache';
const TOKEN_EXPIRATION = 3600 * 1000; // 1 hour in milliseconds
const TOKEN_CHECK_INTERVAL = 60000; // Check every 30 seconds

// ============= Logger =============
const logger = createLogger('AuthContext');

// ============= Context =============
const AuthContext = createContext<AuthContextProps | undefined>(undefined);

// ============= Hook =============
export const UseAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('UseAuth must be used within an AuthProvider');
  }
  return context;
};

// ============= Helper Hook =============
// Safely get MSAL context, returning null values when bypassed
// Note: useMsal() must be called unconditionally (React hooks rule)
// When bypassed, we ignore MSAL values and use mock data instead
const useMsalSafe = () => {
  // Always call useMsal() to satisfy React hooks rules
  const msalContext = useMsal();

  // If bypassed, return safe null values (MSAL won't be used anyway)
  if (isAuthBypassed) {
    return {
      instance: null as IPublicClientApplication | null,
      accounts: [] as AccountInfo[],
      inProgress: 'none' as const,
    };
  }

  return msalContext;
};

// ============= Provider Component =============
export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { instance, accounts, inProgress } = useMsalSafe();
  const [authState, setAuthState] = useState<AuthState>({
    isAuthenticated: false,
    activeAccount: null,
    accessToken: null,
    currentUser: null,
  });
  const [isInitializing, setIsInitializing] = useState(true);
  const bypassInitializedRef = useRef(false);

  const clearTokenCache = useCallback((): void => {
    sessionStorage.removeItem(TOKEN_CACHE_KEY);
  }, []);

  const handleTokenError = useCallback(async () => {
    clearTokenCache();
    setAuthState((prev) => ({
      ...prev,
      isAuthenticated: false,
      activeAccount: null,
      accessToken: null,
      currentUser: null,
    }));
    try {
      if (!isAuthBypassed && instance) {
        await instance.loginRedirect({
          scopes: DEFAULT_SCOPES,
          prompt: 'select_account',
        });
      }
    } catch (e) {
      logger.error('Login redirect after token error failed', { error: e });
    }
  }, [clearTokenCache, instance]);

  const acquireToken = useCallback(
    async (
      account: AccountInfo,
      scopes: string[],
      forceRefresh = false
    ): Promise<TokenResponse | null> => {
      if (!instance) {
        logger.error('MSAL instance not available');
        return null;
      }

      try {
        const response = await instance.acquireTokenSilent({
          account,
          scopes,
          forceRefresh,
        });

        const expiresOn =
          response.expiresOn || new Date(Date.now() + TOKEN_EXPIRATION);

        tokenManager.logTokenInfo(response.accessToken, scopes.join(', '));

        return {
          token: response.accessToken,
          expiresOn,
        };
      } catch (error) {
        if (error instanceof InteractionRequiredAuthError) {
          try {
            const response = await instance.acquireTokenPopup({ scopes });
            return {
              token: response.accessToken,
              expiresOn:
                response.expiresOn || new Date(Date.now() + TOKEN_EXPIRATION),
            };
          } catch (popupError) {
            logger.error('Error acquiring token', { error: popupError });
            await handleTokenError();
            return null;
          }
        }
        logger.error('Error acquiring token silently', { error });
        await handleTokenError();
        return null;
      }
    },
    [instance, handleTokenError]
  );

  const getAccessToken = useCallback(
    async (
      scopes?: string[],
      options: { skipStorageToken?: boolean } = {}
    ): Promise<string | null> => {
      if (isAuthBypassed) return 'mock-token';

      if (!instance) {
        logger.error('MSAL instance not available');
        return null;
      }

      // Ensure we have an active account; if not, try to resolve one
      let account =
        authState.activeAccount || instance.getActiveAccount() || accounts[0];

      if (!account) {
        // Poll briefly for account readiness (e.g., immediately after redirect)
        const maxAttempts = 20;
        const delay = (ms: number) => new Promise((res) => setTimeout(res, ms));
        for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
          const candidate =
            instance.getActiveAccount() ||
            instance.getAllAccounts()[0] ||
            accounts[0];
          if (candidate) {
            account = candidate;
            break;
          }
          // Wait 100ms before next check

          await delay(100);
        }
      }

      if (!account) {
        logger.warning('getAccessToken called before MSAL account was ready');
        return null;
      }

      // Align MSAL active account for future calls and update local state if needed
      try {
        instance.setActiveAccount(account);
      } catch {
        // ignore if MSAL version does not support setActiveAccount
      }
      if (
        !authState.activeAccount ||
        authState.activeAccount.homeAccountId !== account.homeAccountId
      ) {
        setAuthState((prev) => ({ ...prev, activeAccount: account }));
      }

      // Force skipStorageToken to true
      options.skipStorageToken = true;

      // Check if we should skip storage token check
      if (!options.skipStorageToken) {
        // Check if current MSAL token is valid
        if (tokenManager.validateMsalToken()) {
          const tokenData = tokenManager.getMsalTokenFromStorage();
          return tokenData?.secret || null;
        }
      }

      // If token is invalid, expired, or skip is true, acquire new token
      const scopesToUse = scopes || DEFAULT_SCOPES;
      const tokenResponse = await acquireToken(account, scopesToUse);

      if (tokenResponse) {
        return tokenResponse.token;
      }

      return null;
    },
    [authState.activeAccount, acquireToken, instance, accounts]
  );

  const fetchCurrentUser = useCallback(async () => {
    // Only proceed if we have an active account or are in bypass mode
    if (!authState.activeAccount && !isAuthBypassed) {
      logger.debug('No active account available, skipping user fetch');
      return;
    }

    if (isAuthBypassed) {
      // Mock user data for bypass mode - matches the mock account structure
      const mockUser: CurrentUser = {
        userId:
          authState.activeAccount?.localAccountId || 'mock-local-account-id',
        userDisplayName: authState.activeAccount?.name || 'Mock User',
        userEmail: authState.activeAccount?.username || 'mockuser@domain.com',
      };
      setAuthState((prev) => ({
        ...prev,
        currentUser: mockUser,
      }));
      return;
    }

    try {
      // Extract user data from account info
      if (authState.activeAccount) {
        const account = authState.activeAccount;
        const userData: CurrentUser = {
          userId: account.localAccountId || account.homeAccountId,
          userDisplayName: account.name || account.username,
          userEmail: account.username,
          ...(account.idTokenClaims || {}),
        };
        setAuthState((prev) => ({
          ...prev,
          currentUser: userData,
        }));
      }
    } catch (error) {
      logger.error('Error fetching current user', { error });
    }
  }, [authState.activeAccount]);

  const login = async () => {
    if (isAuthBypassed) {
      logger.debug('Bypass mode: Mocking login');
      // Create mock account and set authenticated state
      const mockAccount: AccountInfo = {
        homeAccountId: 'mock-home-account-id',
        environment: 'mock-environment',
        tenantId: 'mock-tenant-id',
        username: 'mockuser@domain.com',
        name: 'Mock User',
        localAccountId: 'mock-local-account-id',
        idTokenClaims: {
          name: 'Mock User',
          oid: 'mock-oid',
          email: 'mockuser@domain.com',
          preferred_username: 'mockuser@domain.com',
        },
      };

      // Create mock user data
      const mockUser: CurrentUser = {
        userId: mockAccount.localAccountId,
        userDisplayName: mockAccount.idTokenClaims?.name || 'Mock User',
        userEmail: mockAccount.username,
      };

      // Update auth state with both account and user data
      // Note: We don't interact with MSAL instance when bypassed
      bypassInitializedRef.current = true;
      setIsInitializing(false);
      setAuthState({
        isAuthenticated: true,
        activeAccount: mockAccount,
        accessToken: 'mock-token',
        currentUser: mockUser,
      });
      return;
    }
    if (!instance) {
      logger.error('MSAL instance not available');
      return;
    }
    try {
      await instance.loginRedirect({
        scopes: DEFAULT_SCOPES,
        prompt: 'select_account',
      });
    } catch (error) {
      logger.error('Login failed', { error });
    }
  };

  const logout = async () => {
    if (isAuthBypassed) {
      logger.debug('Bypass mode: Mocking logout');
      // Clear auth state in bypass mode
      // Note: We don't interact with MSAL instance when bypassed
      bypassInitializedRef.current = false;
      setAuthState({
        isAuthenticated: false,
        activeAccount: null,
        accessToken: null,
        currentUser: null,
      });
      clearTokenCache();
      return;
    }
    if (!instance) {
      logger.error('MSAL instance not available');
      return;
    }
    try {
      await instance.logoutRedirect({
        postLogoutRedirectUri: window.location.origin,
      });
    } catch (error) {
      logger.error('Logout failed', { error });
    } finally {
      setAuthState(() => ({
        isAuthenticated: false,
        activeAccount: null,
        accessToken: null,
        currentUser: null,
      }));
      clearTokenCache();
    }
  };

  useEffect(() => {
    if (!authState.isAuthenticated) return;

    const checkToken = () => {
      const isValid = tokenManager.checkAndHandleTokenExpiration();
      if (!isValid) {
        // Token was expired and cleared, trigger new token acquisition
        void getAccessToken();
      }
    };

    // Check immediately
    checkToken();

    // Set up interval to check periodically
    const interval = setInterval(checkToken, TOKEN_CHECK_INTERVAL); // Check every 60 seconds

    return () => clearInterval(interval);
  }, [authState.isAuthenticated, getAccessToken]);

  useEffect(() => {
    if (isAuthBypassed) {
      // In bypass mode, don't auto-authenticate - user must click login
      // This effect only ensures we don't try to use MSAL accounts
      setIsInitializing(false);
      return;
    }

    const currentAccount = accounts[0];

    const updateAuthState = async () => {
      if (currentAccount && inProgress === 'none') {
        const tokenResponse = await acquireToken(
          currentAccount,
          DEFAULT_SCOPES
        );
        setAuthState((prev) => ({
          ...prev,
          isAuthenticated: true,
          activeAccount: currentAccount,
          accessToken: tokenResponse?.token || null,
          currentUser: null,
        }));
        setIsInitializing(false);
      } else if (!currentAccount && inProgress === 'none') {
        setAuthState((prev) => ({
          ...prev,
          isAuthenticated: false,
          activeAccount: null,
          accessToken: null,
          currentUser: null,
        }));
        setIsInitializing(false);
      }
    };

    void updateAuthState();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accounts.length, inProgress, instance, acquireToken]);

  // Fetch current user when authentication changes
  useEffect(() => {
    // Skip if bypassed (user data is set in the bypass effect)
    if (isAuthBypassed) {
      return;
    }

    if (
      authState.isAuthenticated &&
      authState.activeAccount &&
      !authState.currentUser
    ) {
      void fetchCurrentUser();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authState.isAuthenticated, authState.activeAccount?.homeAccountId]);

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated: authState.isAuthenticated,
        isInitializing,
        activeAccount: authState.activeAccount,
        accessToken: authState.accessToken,
        currentUser: authState.currentUser,
        login,
        logout,
        getAccessToken,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
